import { FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProjectModule } from "@app/projects/literals";
import AIChatPanel from "@app/projects/network-assessment/provider-hub/ai-assistant/AIChatPanel";
import {
  AskQuinnButtonState,
  getAskQuinnDisabledMessage,
} from "@app/projects/network-assessment/provider-hub/ai-assistant/askQuinnButtonGate";

interface QuinnFlyoutProps {
  projectRunConfigurationId: number;
  /** Re-evaluated as the user moves around the project, so it can turn against an open flyout. */
  askQuinnState: AskQuinnButtonState;
  activeProjectModule?: ProjectModule;
  isOnProviderClaimsInsightsTab?: boolean;
}

/**
 * Owns the flyout's mount lifecycle so Quinn can animate both ways.
 *
 * AIChatPanel loads the conversation on mount, so it isn't mounted until Quinn is first opened —
 * users who never open Quinn never fetch a thread. Once opened it stays mounted, and Hide only
 * slides it off-screen: the panel's state is the only record of a turn that is still waiting on
 * Quinn (the server persists a turn only once it is answered), so unmounting on Hide would drop the
 * pending prompt and strand the answer when it lands.
 */
export const QuinnFlyout: FC<QuinnFlyoutProps> = ({ projectRunConfigurationId, askQuinnState, activeProjectModule, isOnProviderClaimsInsightsTab }) => {
  const projectContext = useContext(CountyProjectContext);
  const isOpen = !!projectContext.virtualAssistantContext.isOpen;
  const [hasOpened, setHasOpened] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);

  const disabledReason = askQuinnState.kind === "disabled" ? askQuinnState.reason : undefined;

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
    }
  }, [isOpen]);

  // Quinn can be open when the user navigates somewhere it cannot answer — into compare mode, onto a
  // historical time slice, or while the project starts reprocessing. Leaving it open would let them
  // keep asking questions the run can no longer support, so close it and say why.
  useEffect(() => {
    if (!isOpen || askQuinnState.kind === "enabled") return;

    projectContext.setVirtualAssistantContext({ isOpen: false });

    // Only "disabled" has an explanation to give. "hidden" means the capability is gone entirely
    // (a non-adequacy run, say), where none of the disabled reasons would be true or useful.
    if (disabledReason) {
      toast(getAskQuinnDisabledMessage(disabledReason), {
        containerId: "z-index-9999",
        type: "info",
      });
    }
  }, [isOpen, askQuinnState.kind, disabledReason]);

  // Opening is a two-step: mount closed, let that paint, then flip to open so the browser has a
  // start state to transition from. Going straight to open would just place the panel with no slide.
  useEffect(() => {
    if (!hasOpened || !isOpen) return;

    const frame = window.requestAnimationFrame(() => setIsVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, [hasOpened, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!hasOpened) return null;

  // Keyed by run configuration so a turn still pending against the previous run configuration can't
  // land its answer in the new one's conversation.
  return (
    <AIChatPanel
      key={projectRunConfigurationId}
      projectRunConfigurationId={projectRunConfigurationId}
      isFlyout
      isVisible={isVisible}
      activeProjectModule={activeProjectModule}
      isOnProviderClaimsInsightsTab={isOnProviderClaimsInsightsTab}
    />
  );
};
